import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Constants
const DEFAULT_PAGE_SIZE = 10;
const FILTER_PARAMS = ['specialization', 'status', 'name'];

// Utility function to create auth config
const createAuthConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json"
  }
});

// Utility function to handle error responses
const handleApiError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.response_message ||
                      error.response?.data?.message ||
                      defaultMessage;
  return {
    status: "error",
    message: errorMessage
  };
};

// Utility function to build filter parameters
const buildFilterParams = (filters, page = 0, size = DEFAULT_PAGE_SIZE) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString()
  });

  // Handle filter parameters
  FILTER_PARAMS.forEach(filterKey => {
    if (filters[filterKey]) {
      params.append(filterKey, filters[filterKey]);
    }
  });

  // Handle boolean availableToday filter
  if (filters.availableToday !== undefined) {
    params.append('availableToday', filters.availableToday.toString());
  }

  return params;
};

// Fetch practitioners list with filters and pagination
export const getPractitionersList = createAsyncThunk(
  "practitioners/getPractitionersList",
  async ({ page = 0, size = DEFAULT_PAGE_SIZE, filters = {} }, { rejectWithValue }) => {
    try {
      const params = buildFilterParams(filters, page, size);

      const response = await axios.get(
        `${API_BASE_URL}/admin/counsellors-list?${params.toString()}`,
        createAuthConfig()
      );

      return {
        status: "success",
        data: response.data,
        requestParams: { page, size, filters }
      };
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Failed to fetch practitioners list"));
    }
  }
);

// Fetch practitioner statistics
export const getPractitionerStats = createAsyncThunk(
  "practitioners/getPractitionerStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/practitioner-stats`,
        createAuthConfig()
      );
      return {
        status: "success",
        data: response.data
      };
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Failed to fetch practitioner statistics"));
    }
  }
);

const practitionersSlice = createSlice({
  name: "practitioners",
  initialState: {
    // Statistics data
    stats: {
      data: null,
      loading: false,
      error: null
    },
    // Practitioners list data
    list: {
      data: [],
      totalElements: 0,
      totalPages: 0,
      currentPage: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      loading: false,
      error: null
    },
    // Current filters applied
    filters: {}
  },
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setPageSize: (state, action) => {
      state.list.pageSize = action.payload;
      state.list.currentPage = 0; // Reset to first page
    }
  },
  extraReducers: (builder) => {
    builder
      // Get Practitioner Stats
      .addCase(getPractitionerStats.pending, (state) => {
        state.stats.loading = true;
        state.stats.error = null;
      })
      .addCase(getPractitionerStats.fulfilled, (state, action) => {
        state.stats.loading = false;
        if (action.payload.status === "success") {
          state.stats.data = action.payload.data.payload;
        } else {
          state.stats.error = action.payload.message;
        }
      })
      .addCase(getPractitionerStats.rejected, (state, action) => {
        state.stats.loading = false;
        state.stats.error = action.payload?.message || "Failed to fetch practitioner statistics";
      })

      // Get Practitioners List
      .addCase(getPractitionersList.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(getPractitionersList.fulfilled, (state, action) => {
        state.list.loading = false;
        if (action.payload.status === "success") {
          const responseData = action.payload.data;
          const requestParams = action.payload.requestParams;

          // Handle the API response structure
          if (responseData.payload) {
            state.list.data = responseData.payload.data || [];
            state.list.totalElements = responseData.payload.recordsCount || 0;
            // Calculate total pages from recordsCount and page size
            state.list.totalPages = Math.ceil((responseData.payload.recordsCount || 0) / (requestParams?.size || DEFAULT_PAGE_SIZE));
            state.list.currentPage = requestParams?.page || 0;
          } else {
            state.list.data = [];
            state.list.totalElements = 0;
            state.list.totalPages = 0;
            state.list.currentPage = 0;
          }

          // Update pagination state from request params
          if (requestParams) {
            state.list.pageSize = requestParams.size || state.list.pageSize;
          }
        } else {
          state.list.error = action.payload.message;
        }
      })
      .addCase(getPractitionersList.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload?.message || "Failed to fetch practitioners list";
      });
  },
});

export const { setFilters, clearFilters, setPageSize } = practitionersSlice.actions;
export default practitionersSlice.reducer;